import { useState } from 'react';
import { Link } from 'react-router';
import { useAgentsRh, useStructures } from '@/api/hooks';
import { LIBELLE_REGIME } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr } from '@/lib/utils';

/** Gestion RH (lot RH-1) : agents actifs, régime à confirmer, supérieur hiérarchique, dates ; ouvre la fiche RH. */
export function AgentsRh() {
  const [q, setQ] = useState('');
  const [structure, setStructure] = useState('');
  const [regime, setRegime] = useState('');
  const [sansSuperieur, setSansSuperieur] = useState(false);
  const structures = useStructures();
  const { data, isLoading, error } = useAgentsRh({ q: q || undefined, structure: structure || undefined, regimeConfirme: regime || undefined, sansSuperieur: sansSuperieur ? 'true' : undefined });
  return (
    <div>
      <PageHeader titre="Gestion RH" sousTitre="Fiches RH des agents : grade, supérieur hiérarchique, dates d'engagement et d'ancienneté, régime de congé, éligibilité aux démarches RH." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, matricule, fonction…" value={q} onChange={(e) => setQ(e.target.value)} />
        <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={regime} onChange={(e) => setRegime(e.target.value)}>
          <option value="">Tous les régimes</option>
          <option value="false">Régime à confirmer</option>
          <option value="true">Régime confirmé</option>
        </SelectNatif>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={sansSuperieur} onChange={(e) => setSansSuperieur(e.target.checked)} /> Sans supérieur hiérarchique
        </label>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucun agent ne correspond." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Fonction et grade</TableHead>
                <TableHead>Structure</TableHead>
                <TableHead className="hidden lg:table-cell">Supérieur hiérarchique</TableHead>
                <TableHead className="hidden sm:table-cell">Engagement</TableHead>
                <TableHead>Régime</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Link to={`/rh/agents/${a.id}`} className="font-medium text-primary hover:underline">
                      {a.prenom} {a.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">{a.matricule}{a.eligibleRh ? '' : ' · non éligible'}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {a.fonction}
                    {a.grade && <div className="text-xs text-muted-foreground">{a.grade}</div>}
                  </TableCell>
                  <TableCell>{a.structure?.code ?? '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell">{a.superieur?.nom ?? <span className="text-amber-700">à renseigner</span>}</TableCell>
                  <TableCell className="hidden sm:table-cell">{a.dateEngagement ? dateFr(a.dateEngagement) : <span className="text-amber-700">à renseigner</span>}</TableCell>
                  <TableCell>
                    {a.regimeConge ? <Badge variant="secondary">{LIBELLE_REGIME[a.regimeConge].split(' (')[0]}</Badge> : <Badge variant="outline">à confirmer · proposé : {LIBELLE_REGIME[a.regimePropose].split(' (')[0]}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
